import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, mxn, fmtDate, formatApiError, exportToExcel } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Modal, Btn, Select, Input, EmptyState, ConfirmDialog } from "@/components/Kit";
import { StatusBadge } from "@/components/StatusBadge";
import { LineItemsEditor } from "@/components/LineItemsEditor";
import { TagsField, TagsCell } from "@/components/TagsInput";
import { ReviewModal } from "@/components/ReviewModal";
import { ListSkeleton } from "@/components/Kit";
import { FolioSelector } from "@/components/FolioSelector";
import { FilterBar, matchFilters, sortByFolio, FolioSortHeader } from "@/components/FilterBar";
import { ImportDialog } from "@/components/ImportDialog";
import { useRowSelection, SelectCheckbox, BulkDeleteBar } from "@/components/BulkSelect";
import { usePagination, Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Printer, ChevronDown, Truck, Upload, Download, Copy, Link2, CircleSlash } from "lucide-react";

export default function Cotizaciones() {
  const navigate = useNavigate();
  const { canDo } = useAuth();
  const cCreate = canDo("cotizaciones", "create");
  const cEdit = canDo("cotizaciones", "edit");
  const cConvert = canDo("cotizaciones", "convert");
  const cCancel = canDo("cotizaciones", "cancel");
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [dirigidoA, setDirigidoA] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [ivaRate, setIvaRate] = useState(16);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [validUntil, setValidUntil] = useState("");
  const [totalesFormato, setTotalesFormato] = useState("global");
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [convertOpen, setConvertOpen] = useState(null);
  const [convClientId, setConvClientId] = useState("");
  const [convClientName, setConvClientName] = useState("");
  const [convItems, setConvItems] = useState([]);
  const [convIva, setConvIva] = useState(16);
  const [convNotes, setConvNotes] = useState("");
  const [convTags, setConvTags] = useState([]);
  const [convRequiere, setConvRequiere] = useState(false);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [filters, setFilters] = useState({});
  const [folioDir, setFolioDir] = useState("desc");
  const [importOpen, setImportOpen] = useState(false);

  const [remisiones, setRemisiones] = useState([]);
  const [linkQuote, setLinkQuote] = useState(null);
  const [remSearch, setRemSearch] = useState("");
  const [remSel, setRemSel] = useState("");
  const [linking, setLinking] = useState(false);
  const [folioOpts, setFolioOpts] = useState(null);
  const [folioSel, setFolioSel] = useState("");

  const loadFolioOptions = () => {
    setFolioSel("");
    setFolioOpts(null);
    api.get("/folios/options?prefix=COT").then((r) => setFolioOpts(r.data)).catch(() => setFolioOpts(null));
  };

  const load = () => {
    api.get("/quotes").then((r) => setQuotes(r.data)).finally(() => setLoading(false));
    api.get("/clients").then((r) => setClients([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" }))));
    api.get("/products?active=true").then((r) => setProducts(r.data));
    api.get("/remisiones").then((r) => setRemisiones(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const eid = searchParams.get("edit");
    const did = searchParams.get("duplicate");
    const target = eid || did;
    if (target && quotes.length) {
      const q = quotes.find((x) => x.id === target);
      if (q) {
        eid ? openEdit(q) : openDuplicate(q);
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, quotes]);

  const filteredQuotes = useMemo(
    () => sortByFolio(quotes.filter((q) => matchFilters(q, filters)), folioDir),
    [quotes, filters, folioDir]
  );

  const sel = useRowSelection(filteredQuotes);
  const pageQuotes = usePagination(filteredQuotes, 50, { persist: true });
  const [bulkOpen, setBulkOpen] = useState(false);

  const exportExcel = async () => {
    try {
      const headers = ["Folio", "Cliente", "Fecha", "Remisión", "Tags", "Vencimiento", "Total", "Estatus"];
      const rows = filteredQuotes.map((q) => [
        q.number, q.client?.name || "", fmtDate(q.created_at),
        (q.remision_numbers || []).join(", "), (q.tags || []).join(", "),
        q.valid_until ? fmtDate(q.valid_until) : "", Number(q.total || 0), q.status || "",
      ]);
      const by = {};
      let tc = 0, tt = 0;
      filteredQuotes.forEach((q) => {
        const s = q.status || "Sin estatus";
        by[s] = by[s] || { c: 0, t: 0 };
        by[s].c++; by[s].t += Number(q.total || 0);
        tc++; tt += Number(q.total || 0);
      });
      const summaryRows = Object.entries(by).map(([s, v]) => [s, v.c, Number(v.t.toFixed(2))]);
      summaryRows.push(["TOTAL GENERAL", tc, Number(tt.toFixed(2))]);
      const summary = { title: "Resumen", headers: ["Estatus", "Cantidad", "Total"], rows: summaryRows };
      await exportToExcel("cotizaciones", "Cotizaciones", headers, rows, summary);
    } catch {
      toast.error("No se pudo exportar a Excel");
    }
  };

  const clientOptions = useMemo(
    () => [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })),
    [clients]
  );

  const createProduct = async (payload) => {
    try {
      const { data } = await api.post("/products", payload);
      setProducts((prev) => [data, ...prev]);
      toast.success("Producto creado");
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    }
  };

  const openNew = () => {
    setEditing(null);
    setClientId(clients[0]?.id || "__new__");
    setClientName("");
    setDirigidoA("");
    setItems([{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(16);
    setNotes("Condiciones:\n50% Anticipo\n50% Contra entrega de material\nTiempos de entrega a convenir");
    setTags([]);
    setValidUntil("");
    setTotalesFormato("global");
    loadFolioOptions();
    setOpen(true);
  };

  const openEdit = (q) => {
    setEditing(q.id);
    setClientId(q.client_id || "__new__");
    setClientName(q.client_id ? "" : (q.client_name || q.client?.name || ""));
    setDirigidoA(q.dirigido_a || "");
    setItems(q.items.length ? q.items : [{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(q.iva_rate);
    setNotes(q.notes || "");
    setTags(q.tags || []);
    setValidUntil(q.valid_until || "");
    setTotalesFormato(q.totales_formato || "global");
    setOpen(true);
  };

  const openDuplicate = (q) => {
    setEditing(null);
    setClientId(q.client_id || "__new__");
    setClientName(q.client_id ? "" : (q.client_name || q.client?.name || ""));
    setDirigidoA(q.dirigido_a || "");
    setItems(q.items && q.items.length ? q.items.map((i) => ({ ...i })) : [{ description: "", quantity: 1, unit_price: 0 }]);
    setIvaRate(q.iva_rate ?? 16);
    setNotes(q.notes || "");
    setTags([...(q.tags || [])]);
    setValidUntil(q.valid_until || "");
    setTotalesFormato(q.totales_formato || "global");
    loadFolioOptions();
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const isNew = clientId === "__new__";
    if (isNew && !clientName.trim()) return toast.error("Escribe el nombre del cliente");
    if (!isNew && !clientId) return toast.error("Selecciona un cliente");
    if (!tags || tags.length === 0) return toast.error("Agrega al menos un tag (etiqueta) para guardar el documento");
    const payload = {
      client_id: isNew ? null : clientId,
      client_name: isNew ? clientName.trim() : "",
      dirigido_a: dirigidoA.trim(),
      items, iva_rate: ivaRate, notes, tags, valid_until: validUntil, totales_formato: totalesFormato,
      ...(editing ? {} : { folio: folioSel || null }),
    };
    try {
      if (editing) {
        await api.put(`/quotes/${editing}`, payload);
        toast.success("Cotización actualizada");
      } else {
        const { data } = await api.post("/quotes", payload);
        if (folioSel && data?.number && data.number !== folioSel) {
          toast.info(`El folio ${folioSel} ya se ocupó; se asignó ${data.number}`);
        } else {
          toast.success("Cotización creada");
        }
        setOpen(false);
        if (data?.id) return navigate(`/documento/cotizacion/${data.id}`);
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changeStatus = async (id, status) => {
    await api.patch(`/quotes/${id}/status`, { status });
    if (status === "Aprobada") {
      const q = quotes.find((x) => x.id === id);
      if (q) {
        toast.success(`Cotización ${q.number} aprobada · genera la remisión`);
        navigate("/remisiones", { state: { fromQuote: { ...q, status: "Aprobada" } } });
        return;
      }
    }
    load();
  };

  const openLinkRemision = (q) => {
    setLinkQuote(q);
    setRemSel("");
    setRemSearch("");
  };

  const filteredRemisiones = useMemo(() => {
    const s = remSearch.trim().toLowerCase();
    const list = [...remisiones].sort((a, b) => (b.number || "").localeCompare(a.number || "", "es"));
    if (!s) return list.slice(0, 50);
    return list
      .filter((r) => (r.number || "").toLowerCase().includes(s) || (r.client?.name || "").toLowerCase().includes(s))
      .slice(0, 50);
  }, [remisiones, remSearch]);

  const confirmLinkRemision = async () => {
    if (!remSel) return toast.error("Selecciona una remisión");
    setLinking(true);
    try {
      const { data } = await api.post(`/quotes/${linkQuote.id}/link-remision`, { remision_id: remSel });
      toast.success(`Remisión ${data.remision_number || ""} vinculada a la cotización ${linkQuote.number}`);
      setLinkQuote(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLinking(false);
    }
  };

  const openConvert = (q) => {
    navigate("/remisiones", { state: { fromQuote: q } });
  };

  const convert = async (e) => {
    if (e) e.preventDefault();
    if (!convertOpen) return;
    const isNew = convClientId === "__new__";
    if (isNew && !convClientName.trim()) return toast.error("Escribe el nombre del cliente");
    if (!isNew && !convClientId) return toast.error("Selecciona un cliente");
    if (!convTags || convTags.length === 0) return toast.error("Agrega al menos un tag (etiqueta) para guardar el documento");
    const payload = {
      client_id: isNew ? null : convClientId,
      client_name: isNew ? convClientName.trim() : "",
      items: convItems, iva_rate: convIva, notes: convNotes,
      requiere_factura: convRequiere,
      tags: convTags,
    };
    try {
      const { data } = await api.post(`/quotes/${convertOpen.id}/convert-to-remision`, payload);
      toast.success("Remisión creada");
      setConvertOpen(null);
      setReviewDoc(data);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!deleteOpen) return;
    try {
      await api.delete(`/quotes/${deleteOpen.id}`);
      toast.success(`Cotización ${deleteOpen.number} eliminada`);
      setDeleteOpen(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const bulkRemove = async () => {
    try {
      await api.post(`/quotes/bulk-delete`, { ids: sel.selectedIds });
      toast.success(`${sel.selectedIds.length} cotizaciones eliminadas`);
      sel.clear();
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <>
      <PageHeader
        overline="Ciclo de venta · Paso 1"
        title="Cotizaciones"
        action={
          <div className="flex gap-3">
            <Btn variant="outline" onClick={exportExcel} data-testid="export-quotes-button" disabled={quotes.length === 0}>
              <Download size={16} /> Exportar a Excel
            </Btn>
            {cCreate && (
              <Btn variant="outline" onClick={() => setImportOpen(true)} data-testid="import-quotes-button">
                <Upload size={16} /> Importar
              </Btn>
            )}
            {cCreate && (
              <Btn onClick={openNew} data-testid="new-quote-button">
                <Plus size={16} /> Nueva cotización
              </Btn>
            )}
          </div>
        }
      />
      <div className="p-10">
        {loading ? (
          <ListSkeleton testid="cotizaciones-loading" />
        ) : clients.length === 0 ? (
          <EmptyState
            title="Primero agrega clientes"
            subtitle="Necesitas al menos un cliente para crear una cotización."
          />
        ) : quotes.length === 0 ? (
          <EmptyState
            title="Sin cotizaciones aún"
            subtitle="Crea tu primera cotización para iniciar el flujo de trabajo."
            action={<Btn onClick={openNew}><Plus size={16} /> Nueva cotización</Btn>}
          />
        ) : (
          <>
          <FilterBar clients={clients} reportType="cotizaciones" onApply={setFilters} statuses={[...new Set(quotes.map((q) => q.status).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"))} showTags />
          {cCancel && <BulkDeleteBar count={sel.selectedIds.length} onDelete={() => setBulkOpen(true)} testid="bulk-delete-quotes" />}
          <div className="border border-[#E3E8EE] bg-[#FFFFFF] data-scroll">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3E8EE] text-left">
                  <Th className="w-10"><SelectCheckbox checked={sel.allChecked} onChange={sel.toggleAll} testid="select-all-quotes" /></Th>
                  <Th><FolioSortHeader dir={folioDir} onToggle={() => setFolioDir((d) => d === "desc" ? "asc" : "desc")} /></Th>
                  <Th>Cliente</Th>
                  <Th>Fecha</Th>
                  <Th>Remisión</Th>
                  <Th>Tags</Th>
                  <Th>Vencimiento</Th>
                  <Th className="text-right">Total</Th>
                  <Th>Estatus</Th>
                  <Th className="text-right">Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {pageQuotes.pageItems.map((q) => {
                  const alerta = ["Aprobada", "Borrador"].includes(q.status) ? null : q.alert;
                  return (
                  <tr key={q.id} data-testid={`quote-row-${q.number}`} onClick={() => navigate(`/documento/cotizacion/${q.id}`)} className={`border-b border-[#E3E8EE] last:border-0 transition-colors cursor-pointer ${alerta === "vencida" ? "bg-[#FBEAEA] hover:bg-[#F7DEDE]" : alerta === "por_vencer" ? "bg-[#FCF6E3] hover:bg-[#F7EFD2]" : "hover:bg-[#E9EEF3]"}`}>
                    <Td onClick={(e) => e.stopPropagation()}>{cCancel && <SelectCheckbox checked={sel.selected.has(q.id)} onChange={() => sel.toggle(q.id)} testid={`select-quote-${q.number}`} />}</Td>
                    <Td className="text-[#1C1A17] font-medium">{q.number}</Td>
                    <Td>{q.client?.name || "—"}</Td>
                    <Td>{fmtDate(q.created_at)}</Td>
                    <Td>
                      {q.remision_numbers && q.remision_numbers.length > 0 ? (
                        <span className="inline-flex flex-wrap gap-1" data-testid={`quote-remisiones-${q.number}`}>
                          {q.remision_numbers.map((rn) => (
                            <span key={rn} className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-[#49AC9A]/15 text-[#2f7a6c] border border-[#49AC9A]/30 whitespace-nowrap">
                              <Truck size={11} /> {rn}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-[#9aa3ac]">—</span>
                      )}
                    </Td>
                    <Td><TagsCell tags={q.tags} testid={`quote-tags-${q.number}`} /></Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <span>{q.valid_until ? fmtDate(q.valid_until) : "—"}</span>
                        {alerta === "vencida" && <span data-testid={`quote-alert-${q.number}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#ED1F26] text-white">Vencida</span>}
                        {alerta === "por_vencer" && <span data-testid={`quote-alert-${q.number}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#B8860B] text-white">Próxima a vencer</span>}
                      </div>
                    </Td>
                    <Td className="text-right text-[#1C1A17]">{mxn(q.total)}</Td>
                    <Td>
                      <div onClick={(e) => e.stopPropagation()}>
                      {cEdit && q.status !== "Anulado" ? (
                        <StatusSelect value={q.status} onChange={(s) => changeStatus(q.id, s)}
                          options={["Borrador", "Enviada", "Aprobada", "Rechazada"]} testid={`quote-status-${q.number}`} />
                      ) : (
                        <StatusBadge status={q.status} />
                      )}
                      </div>
                    </Td>
                    <Td className="text-right whitespace-nowrap">
                      <div onClick={(e) => e.stopPropagation()}>
                      {cConvert && <IconBtn title="Convertir a remisión" onClick={() => openConvert(q)} testid={`convert-quote-${q.number}`}><Truck size={15} /></IconBtn>}
                      {cEdit && <IconBtn title="Vincular remisión" onClick={() => openLinkRemision(q)} testid={`link-remision-quote-${q.number}`}><Link2 size={15} /></IconBtn>}
                      <a href={`/imprimir/cotizacion/${q.id}`} target="_blank" rel="noreferrer" className="inline-block text-[#5C5A55] hover:text-[#465B7F] mx-1.5" title="PDF" data-testid={`print-quote-${q.number}`}><Printer size={15} /></a>
                      {cCreate && <IconBtn title="Duplicar" onClick={() => openDuplicate(q)} testid={`duplicate-quote-${q.number}`}><Copy size={15} /></IconBtn>}
                      {cEdit && <IconBtn title="Editar" onClick={() => openEdit(q)}><Pencil size={15} /></IconBtn>}
                      {cCancel && <IconBtn title="Anular Documento" tone="anular" onClick={() => setDeleteOpen(q)} testid={`delete-quote-${q.number}`}><CircleSlash size={15} /></IconBtn>}
                      </div>
                    </Td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination {...pageQuotes} testid="pagination-quotes" />
          </>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar cotización" : "Nueva cotización"} xl logo>
        <form onSubmit={save} className="space-y-5">
          {!editing && <FolioSelector options={folioOpts} value={folioSel} onChange={setFolioSel} />}
          <div className="grid grid-cols-2 gap-4">
            <Select label="Cliente *" value={clientId} onChange={(e) => setClientId(e.target.value)} testid="quote-client-select">
              <option value="__new__">➕ Cliente nuevo (sin registrar)</option>
              {clientOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>
              ))}
            </Select>
            <Input label="Válida hasta" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} testid="quote-valid-until" />
          </div>
          {clientId === "__new__" && (
            <div className="border border-[#465B7F]/30 bg-[#465B7F]/5 p-4">
              <Input label="Nombre del cliente nuevo *" value={clientName} onChange={(e) => setClientName(e.target.value)} testid="quote-client-name" placeholder="Ej. Comercializadora del Norte" />
              <p className="font-mono-data text-xs text-[#5B6672] mt-2">
                Este cliente <b>no</b> se dará de alta ahora. Se registrará automáticamente al elaborar la remisión de esta cotización.
              </p>
            </div>
          )}
          <Input label="Dirigido a:" value={dirigidoA} onChange={(e) => setDirigidoA(e.target.value)} testid="quote-dirigido-a" placeholder="Ej. Ing. Juan Pérez / Depto. de Compras" />
          <LineItemsEditor items={items} setItems={setItems} ivaRate={ivaRate} setIvaRate={setIvaRate} products={products} onCreateProduct={createProduct} />
          <div>
            <span className="overline block mb-2">Formato de totales</span>
            <div className="grid grid-cols-2 gap-3" data-testid="quote-totales-formato">
              {[
                { v: "independientes", t: "Totales independientes", d: "Desglose por concepto (importe, IVA y total). Sin gran total al pie." },
                { v: "global", t: "Total global", d: "Desglose por concepto + resumen final (subtotal, IVA y gran total)." },
              ].map((o) => (
                <label key={o.v} data-testid={`quote-totales-${o.v}`}
                  className={`flex gap-3 p-3 border cursor-pointer transition-colors ${totalesFormato === o.v ? "border-[#465B7F] bg-[#465B7F]/5" : "border-[#E3E8EE] hover:border-[#465B7F]/50"}`}>
                  <input type="radio" name="totales-formato" value={o.v} checked={totalesFormato === o.v}
                    onChange={() => setTotalesFormato(o.v)} className="mt-0.5 accent-[#465B7F]" />
                  <span>
                    <span className="block font-mono-data text-sm text-[#1C1A17]">{o.t}</span>
                    <span className="block font-mono-data text-xs text-[#5B6672] mt-0.5">{o.d}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="overline block mb-1.5">Notas</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="quote-notes"
              className="w-full bg-[#FFFFFF] border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <TagsField tags={tags} setTags={setTags} testid="quote-tags-input" />
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-quote-button">Guardar cotización</Btn>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteOpen}
        onClose={() => setDeleteOpen(null)}
        onConfirm={remove}
        title="Anular cotización"
        message={`¿Anular la cotización ${deleteOpen?.number}? Cambiará a estatus "Anulado" y conservará su folio (no se elimina de la base de datos).`}
        confirmLabel="Anular cotización"
        testid="confirm-delete-quote-button"
      />

      <Modal open={!!convertOpen} onClose={() => setConvertOpen(null)} title={`Convertir a remisión · ${convertOpen?.number || ""}`} xl logo>
        <form onSubmit={convert} className="space-y-5">
          <p className="font-mono-data text-xs text-[#5B6672] bg-[#465B7F]/5 border border-[#465B7F]/20 px-3 py-2">
            Revisa y edita los datos antes de generar la remisión. La cotización quedará como <b>Aprobada</b>.
          </p>
          <Select label="Cliente *" value={convClientId} onChange={(e) => setConvClientId(e.target.value)} testid="convert-client-select">
            <option value="__new__">➕ Cliente nuevo (sin registrar)</option>
            {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
          </Select>
          {convClientId === "__new__" && (
            <div className="border border-[#465B7F]/30 bg-[#465B7F]/5 p-4">
              <Input label="Nombre del cliente nuevo *" value={convClientName} onChange={(e) => setConvClientName(e.target.value)} testid="convert-client-name" placeholder="Ej. Comercializadora del Norte" />
              <p className="font-mono-data text-xs text-[#5B6672] mt-2">
                Al generar la remisión, este cliente se dará de alta automáticamente (si aún no existe).
              </p>
            </div>
          )}
          <div>
            <span className="overline block mb-2">¿Requiere factura?</span>
            <div className="flex gap-3" data-testid="convert-requiere-factura">
              {[{ v: true, t: "Sí, se facturará" }, { v: false, t: "No requiere factura" }].map((o) => (
                <label key={String(o.v)} data-testid={`convert-requiere-${o.v ? "si" : "no"}`}
                  className={`flex items-center gap-2 px-4 py-2.5 border cursor-pointer transition-colors ${convRequiere === o.v ? "border-[#465B7F] bg-[#465B7F]/5" : "border-[#E3E8EE] hover:border-[#465B7F]/50"}`}>
                  <input type="radio" name="convert-requiere-factura" checked={convRequiere === o.v}
                    onChange={() => setConvRequiere(o.v)} className="accent-[#465B7F]" />
                  <span className="font-mono-data text-sm text-[#1C1A17]">{o.t}</span>
                </label>
              ))}
            </div>
          </div>
          <LineItemsEditor items={convItems} setItems={setConvItems} ivaRate={convIva} setIvaRate={setConvIva} products={products} onCreateProduct={createProduct} />
          <label className="block">
            <span className="overline block mb-1.5">Notas</span>
            <textarea value={convNotes} onChange={(e) => setConvNotes(e.target.value)} rows={2} data-testid="convert-notes"
              className="w-full bg-white border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm focus:outline-none focus:ring-1 focus:ring-[#465B7F]" />
          </label>
          <TagsField tags={convTags} setTags={setConvTags} testid="convert-tags-input" />
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setConvertOpen(null)}>Cancelar</Btn>
            <Btn type="submit" data-testid="confirm-convert-quote-button">Generar remisión</Btn>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onConfirm={bulkRemove}
        title="Anular cotizaciones"
        message={`¿Anular ${sel.selectedIds.length} cotización(es)? Cambiarán a estatus "Anulado" y conservarán su folio.`}
        confirmLabel="Anular seleccionados"
        testid="confirm-bulk-delete-quotes-button"
      />

      <ReviewModal
        open={!!reviewDoc}
        type="remision"
        doc={reviewDoc}
        clients={clients}
        products={products}
        onCreateProduct={createProduct}
        onClose={() => { setReviewDoc(null); load(); }}
        onAccepted={load}
      />

      <Modal open={!!linkQuote} onClose={() => setLinkQuote(null)} title={`Vincular remisión · ${linkQuote?.number || ""}`} wide logo>
        <div className="space-y-4">
          <p className="font-mono-data text-sm text-[#5B6672]">
            Selecciona una remisión existente para vincularla a esta cotización. El folio aparecerá en la columna "Remisión".
          </p>
          <Input
            label="Buscar remisión (por folio o cliente)"
            value={remSearch}
            onChange={(e) => setRemSearch(e.target.value)}
            testid="link-remision-search"
            placeholder="Ej. R-1360 o nombre del cliente"
          />
          <div className="border border-[#E3E8EE] divide-y divide-[#E3E8EE] max-h-72 overflow-auto" data-testid="link-remision-list">
            {filteredRemisiones.length === 0 ? (
              <p className="font-mono-data text-xs text-[#5B6672] px-3 py-6 text-center">Sin remisiones que coincidan.</p>
            ) : filteredRemisiones.map((r) => (
              <label key={r.id} data-testid={`link-remision-option-${r.number}`}
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#E9EEF3]">
                <input type="radio" name="link-remision" checked={remSel === r.id} onChange={() => setRemSel(r.id)}
                  className="h-4 w-4 accent-[#465B7F]" />
                <span className="font-mono-data text-sm text-[#16202B] w-28">{r.number}</span>
                <span className="font-mono-data text-xs text-[#5B6672] flex-1 truncate">{r.client?.name || "—"}</span>
                <span className="font-mono-data text-xs text-[#5B6672]">{fmtDate(r.created_at)}</span>
                <span className="font-mono-data text-sm text-[#16202B]">{mxn(r.total)}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setLinkQuote(null)}>Cancelar</Btn>
            <Btn type="button" onClick={confirmLinkRemision} disabled={linking || !remSel} data-testid="confirm-link-remision-quote-button">
              {linking ? "Vinculando…" : "Vincular remisión"}
            </Btn>
          </div>
        </div>
      </Modal>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} module="quotes" title="Importar cotizaciones" onDone={load} />
    </>
  );
}

const QUOTE_SELECT_STYLES = {
  aprobada: "bg-[#22C55E] text-white border-[#16A34A]",
  rechazada: "bg-[#EF4444] text-white border-[#DC2626]",
  vencida: "bg-[#EF4444] text-white border-[#DC2626]",
  anulado: "bg-[#EF4444] text-white border-[#DC2626]",
  enviada: "bg-[#465B7F] text-white border-[#374A66]",
  borrador: "bg-[#94A3B8] text-white border-[#64748B]",
  "por enviar": "bg-[#94A3B8] text-white border-[#64748B]",
};

export function StatusSelect({ value, onChange, options, testid }) {
  const cls = QUOTE_SELECT_STYLES[String(value || "").trim().toLowerCase()] || "bg-[#94A3B8] text-white border-[#64748B]";
  return (
    <div className="relative inline-block">
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className={`appearance-none border rounded pl-2 pr-6 py-1 font-mono-data text-xs font-bold uppercase tracking-wider cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#465B7F] ${cls}`}>
        {options.map((o) => <option key={o} value={o} className="bg-white text-[#16202B] font-normal normal-case">{o === "Borrador" ? "Por enviar" : o}</option>)}
      </select>
      <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-white" />
    </div>
  );
}

export function IconBtn({ children, onClick, danger, tone, title, testid, disabled }) {
  const cls = disabled
    ? "text-stone-300 cursor-not-allowed"
    : tone === "anular"
    ? "text-[#C77B7B] hover:text-[#B0656A]"
    : danger
    ? "text-[#5C5A55] hover:text-[#ED1F26]"
    : "text-[#5C5A55] hover:text-[#465B7F]";
  return (
    <button onClick={onClick} title={title} data-testid={testid} disabled={disabled}
      className={`inline-block mx-1.5 ${cls}`}>
      {children}
    </button>
  );
}

function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "", onClick }) {
  return <td onClick={onClick} className={`font-mono-data text-sm text-[#5C5A55] px-4 py-3 ${className}`}>{children}</td>;
}
